import { Fragment, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Container } from "reactstrap";
import { editCompanyDetails, fetchCompanyDetails, getMerchantByID } from "../../../utils/apiCalls";
import { errorToast, successToast } from "../../../shared/Toast";
import { C, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { Badge, Loader, RCard } from "../../reporting/ReportingShared";
import Breadcrumb from "../../common/breadcrumb";
import ReceiptLayoutEditor from "./ReceiptLayoutEditor";
import ReceiptPreview from "./ReceiptPreview";

const ghostBtn = {
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    color: C.textMed,
    fontWeight: 600,
    fontSize: 12,
    padding: "8px 16px",
};
const primaryBtn = {
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
    border: "none",
    borderRadius: 8,
    color: "#fff",
    fontWeight: 700,
    fontSize: 12,
    padding: "8px 18px",
};

/**
 * How a merchant's self-order screens print the customer receipt.
 *
 * Stored on the merchant's company configuration (`CompanyConfigs.receiptLayout`), so every screen of
 * the merchant follows one edit. The page edits the resolved layout the API sends and saves only the
 * layout — the rest of the company record is left to the Company Configuration page.
 */
const ReceiptLayoutPage = () => {
    const userType = localStorage.getItem("userType");
    const merchantId = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;
    const hasMerchant = !!selectedMerchant && selectedMerchant !== "all";

    const [isLoading, setIsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [company, setCompany] = useState(null);
    const [layout, setLayout] = useState(null);
    const [defaultLayout, setDefaultLayout] = useState(null);
    const [customised, setCustomised] = useState(false);
    const [dirty, setDirty] = useState(false);
    // Reset is only a request until saved: the page shows the default, and Save sends `null`, so the
    // merchant keeps following the default as it evolves rather than a frozen copy of it.
    const [resetPending, setResetPending] = useState(false);
    const [logoUrl, setLogoUrl] = useState(null);

    const load = useCallback(async () => {
        setCompany(null);
        setLayout(null);
        setDirty(false);
        setResetPending(false);
        setLogoUrl(null);
        if (!hasMerchant) return;

        setIsLoading(true);
        try {
            const response = await fetchCompanyDetails();
            const found = (response?.data || []).find((c) => String(c.merchantId) === String(selectedMerchant));
            setCompany(found || null);
            setLayout(found?.receiptLayoutResolved || null);
            setDefaultLayout(found?.receiptLayoutDefault || null);
            setCustomised(found?.receiptLayout != null);

            // Only for the preview; the page works without it.
            getMerchantByID(selectedMerchant)
                .then((res) => {
                    const logo = res?.data?.receiptLogo;
                    if (logo) setLogoUrl(process.env.REACT_APP_URL + logo);
                })
                .catch(() => {});
        } catch (e) {
            errorToast("Failed to load the receipt layout");
        } finally {
            setIsLoading(false);
        }
    }, [hasMerchant, selectedMerchant]);

    useEffect(() => {
        load();
    }, [load]);

    const handleChange = (next) => {
        setLayout(next);
        setDirty(true);
        setResetPending(false);
    };

    const handleReset = () => {
        setLayout(defaultLayout);
        setDirty(true);
        setResetPending(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await editCompanyDetails({
                merchantId: company.merchantId,
                receiptLayout: resetPending ? null : layout,
            });
            if (response?.isError === false) {
                successToast("Receipt layout saved. Screens pick it up within a minute.");
                await load();
            } else {
                errorToast(response?.message || "Failed to save the receipt layout");
            }
        } finally {
            setSaving(false);
        }
    };

    const status = resetPending
        ? { label: "Resets to default on save", color: C.orange, bg: C.orangeLight }
        : dirty
            ? { label: "Unsaved changes", color: C.orange, bg: C.orangeLight }
            : customised
                ? { label: "Customised", color: C.coral, bg: C.coralLight }
                : { label: "Default layout", color: C.textMed, bg: C.bg };

    const renderBody = () => {
        if (!hasMerchant) {
            return (
                <RCard>
                    <p style={{ textAlign: "center", padding: 40, color: C.textLight, margin: 0 }}>Select a merchant</p>
                </RCard>
            );
        }
        if (isLoading) return <RCard><Loader /></RCard>;
        if (!company) {
            return (
                <RCard>
                    <p style={{ textAlign: "center", padding: 40, color: C.textLight, margin: 0 }}>
                        This merchant has no company configuration yet. Create it under Settings → Company
                        Configuration first — the receipt prints its name and VAT number.
                    </p>
                </RCard>
            );
        }
        if (!layout) {
            return (
                <RCard>
                    <p style={{ textAlign: "center", padding: 40, color: C.textLight, margin: 0 }}>
                        The server did not return a receipt layout. It may need updating.
                    </p>
                </RCard>
            );
        }

        return (
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 380px", gap: 20, alignItems: "start" }}>
                <RCard title="🧾 Layout">
                    <ReceiptLayoutEditor value={layout} onChange={handleChange} />
                </RCard>
                <div style={{ position: "sticky", top: 90 }}>
                    <RCard title="👁️ Live Preview">
                        <ReceiptPreview layout={layout} company={company} logoUrl={logoUrl} />
                    </RCard>
                </div>
            </div>
        );
    };

    return (
        <Fragment>
            <style>{reportingGlobalCSS}</style>
            <Breadcrumb title="Receipt Layout" parent="Merchants" />
            <Container fluid className="rpt-wrap">
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Self-order receipt</div>
                        <div style={{ fontSize: 12, color: C.textLight }}>
                            How this merchant&apos;s self-order screens print the customer&apos;s tax invoice. One layout for every screen.
                        </div>
                    </div>
                    {!hasMerchant && (
                        <span style={{ color: C.orange, fontWeight: 600, fontSize: 13 }}>Please select a merchant from the dropdown</span>
                    )}
                    <div style={{ flex: 1 }} />
                    {company && layout && (
                        <>
                            <Badge color={status.color} bg={status.bg}>{status.label}</Badge>
                            {defaultLayout && (
                                <Button size="sm" style={ghostBtn} onClick={handleReset} disabled={saving}>
                                    Reset to default
                                </Button>
                            )}
                            <Button size="sm" style={ghostBtn} onClick={load} disabled={saving || !dirty}>
                                Discard
                            </Button>
                            <Button size="sm" style={{ ...primaryBtn, opacity: dirty ? 1 : 0.5 }} onClick={handleSave} disabled={saving || !dirty}>
                                {saving ? "Saving…" : "Save"}
                            </Button>
                        </>
                    )}
                </div>

                {renderBody()}
            </Container>
        </Fragment>
    );
};

export default ReceiptLayoutPage;
